import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output
} from "@angular/core";
import {VisitScheduleResourceModel} from "../../../models/visit-schedule-resource.model";
import {TranslateService} from "@ngx-translate/core";
import * as moment from 'moment-timezone';
import {VisitPlanModel} from "../../../models/visit-plan.model";
import {VisitScheduleResourceHolderExt} from "../../../models/visit-schedule-resource-holder-ext.model";

require('./availability-table-row.scss');
@Component({
  standalone: false,
  selector: 'app-availability-table-row',
  templateUrl: './availability-table-row.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AvailabilityTableRowComponent implements OnInit, OnChanges {

  @Input() agent: VisitScheduleResourceHolderExt;
  @Input() index: number;
  @Input() dates: string[];
  @Input() resourcesByDate?: Map<string, VisitScheduleResourceModel[]>;
  @Input() conflictingResourceIds?: Set<string>;
  @Input() visitPlan?: VisitPlanModel;
  @Input() visitScheduleId?: string;
  @Input() zoneId?: string;

  @Output() resourcesUpdated = new EventEmitter<VisitScheduleResourceModel[]>();
  @Output() disableAllDays = new EventEmitter<string>();

  dateInfo: Map<string, {
    dayName: string;
    formattedDate: string;
    resources: VisitScheduleResourceModel[];
    hasConflict: boolean;
    hasAvailability: boolean;
    hasLeave: boolean;
    availabilityData: { available: boolean; timeWindow?: {from,to}; notAvailable?: boolean };
  }> = new Map();

  constructor(
    @Inject(TranslateService) private translateService: TranslateService
  ) {
  }

  ngOnInit() {
    this._computeDateInfo();
  }

  ngOnChanges() {
    this._computeDateInfo();
  }

  private _computeDateInfo() {
    const info = new Map();
    (this.dates || []).forEach(date => {
      const resources = this.resourcesByDate?.get(date) || [];
      info.set(date, {
        dayName: this._getDayName(date),
        formattedDate: this._getFormattedDate(date),
        resources,
        hasConflict: resources.some(r => this.conflictingResourceIds?.has(r.id)),
        hasAvailability: resources.some(r => r.scheduleType === 'Availability' || r.scheduleType === 'Shift'),
        hasLeave: resources.some(r => r.scheduleType === 'Leave'),
        availabilityData: this._getAvailabilityData(date, resources)
      });
    });
    this.dateInfo = info;
  }

  onDisableAllDays() {
    this.disableAllDays.emit(this.agent.id);
  }

  private _formatTime(timestamp: number): string {
    if (this.zoneId) {
      return moment(timestamp).tz(this.zoneId).format('HH:mm');
    }
    const date = new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private _getTimeWindow(resources: VisitScheduleResourceModel[]): {from, to} {
    if (!resources.length) {
      return;
    }
    const validResources = resources.filter(r => r.startTime != null && r.endTime != null);
    if (validResources.length === 0) {
      return;
    }
    const startTime = Math.min(...validResources.map(r => r.startTime));
    const endTime = Math.max(...validResources.map(r => r.endTime));
    return { from: startTime, to: endTime };
  }

  private _getDayName(dateString: string): string {
    const date = new Date(dateString);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
  }

  private _getFormattedDate(dateString: string): string {
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${month}/${day}`;
  }

  private _getAvailabilityData(date: string, resources: VisitScheduleResourceModel[]): { available: boolean; timeWindow?: {from,to}; notAvailable?: boolean } {
    const timeWindow = this._getTimeWindow(resources);
    const hasValidTimeResources = resources.some(r => r.startTime != null && r.endTime != null);
    const hasEnabledResources = resources.some(r => r.disabled === false);
    const hasDisabledResources = resources.some(r => r.disabled === true);
    const available = hasEnabledResources;
    const notAvailable = !available && hasDisabledResources;
    return {
      available,
      timeWindow: hasValidTimeResources ? timeWindow : undefined,
      notAvailable
    };
  }

  onResourcesUpdated(resources: VisitScheduleResourceModel[]) {
    this.resourcesUpdated.emit(resources);
  }

}

